import { Controller } from "@hotwired/stimulus"

// This controller is nearly identical to contributor_roles_controller
// but uses "track" instead of "album" for form field names
export default class extends Controller {
  static targets = ["rolesContainer", "hiddenFields"]
  static values = { organizationId: Number }

  connect() {
    this.roleBlockCounter = 0
    this.activeRoles = new Set()

    // Track already rendered role blocks
    const existingRoleBlocks = this.rolesContainerTarget.querySelectorAll('[data-role-key]')
    existingRoleBlocks.forEach(block => {
      const roleKey = block.dataset.roleKey
      this.activeRoles.add(roleKey)
    })

    // Find the select element associated with this controller instance
    // Look for select in the same parent container
    const containerElement = this.element
    const selectElement = containerElement.querySelector('select[name*="_role_select"]')

    if (selectElement) {
      this.setupRoleDropdown(selectElement)
    }
  }

  addRoleBlock(roleKey, roleName) {
    // Don't add if already exists
    if (this.activeRoles.has(roleKey)) {
      return
    }

    this.activeRoles.add(roleKey)
    const blockId = `role_block_${roleKey}_${this.roleBlockCounter++}`

    // Create the role block HTML
    const roleBlock = document.createElement('div')
    roleBlock.id = blockId
    roleBlock.className = 'bg-gray-50 p-4 rounded-lg mt-3 border border-gray-300'
    roleBlock.dataset.roleKey = roleKey

    roleBlock.innerHTML = `
      <div class="flex justify-between items-center mb-3">
        <h4 class="text-sm font-medium text-gray-500">${roleName}</h4>
        <button type="button"
                class="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                data-action="click->track-contributor-roles#removeRole"
                data-role-key="${roleKey}">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <div class="space-y-3" data-controller="track-role-artist-search"
           data-track-role-artist-search-organization-id-value="${this.organizationIdValue}"
           data-track-role-artist-search-role-key-value="${roleKey}"
           data-track-role-artist-search-role-name-value="${roleName}">

        <!-- Selected Artists Tags -->
        <div data-track-role-artist-search-target="selectedArtists" class="space-y-2">
          <!-- Artists will be added here -->
        </div>

        <!-- Hidden inputs container -->
        <div data-track-role-artist-search-target="hiddenInputs">
          <!-- Hidden inputs for form submission will be added here -->
        </div>

        <!-- Artist Search Dropdown -->
        <div class="relative">
          <div class="relative">
            <input type="text"
                   data-track-role-artist-search-target="searchInput"
                   data-action="input->track-role-artist-search#search focus->track-role-artist-search#showDropdown"
                   placeholder="+ Add a Contributor for ${roleName}"
                   class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors">

            <!-- Dropdown -->
            <div data-track-role-artist-search-target="dropdown"
                 class="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg hidden">

              <!-- Results -->
              <ul data-track-role-artist-search-target="results" class="max-h-60 overflow-auto">
                <!-- Results will be populated here -->
              </ul>

              <!-- Loading indicator -->
              <div data-track-role-artist-search-target="loading" class="hidden p-3 text-center text-gray-500">
                <div class="inline-flex items-center">
                  <svg class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `

    // Add the block to the container (append to add new blocks at the bottom)
    this.rolesContainerTarget.appendChild(roleBlock)
  }

  removeRole(event) {
    event.preventDefault()
    event.stopPropagation()

    const roleKey = event.currentTarget.dataset.roleKey
    const roleBlock = event.currentTarget.closest('div.bg-gray-50[data-role-key]')

    if (roleBlock) {
      // Remove all hidden inputs for this role
      const hiddenInputs = this.hiddenFieldsTarget.querySelectorAll(`input[name^="track[contributor_roles][${roleKey}]"]`)
      hiddenInputs.forEach(input => input.remove())

      // Remove the role block from the UI
      roleBlock.remove()
      this.activeRoles.delete(roleKey)
    }
  }

  setupRoleDropdown(selectElement) {
    // Wait for TomSelect to initialize
    setTimeout(() => {
      if (selectElement.tomselect) {
        const tomselect = selectElement.tomselect

        tomselect.on('item_add', (value, item) => {
          const roleName = item.textContent
          this.addRoleBlock(value, roleName)

          // Clear the selection and blur the dropdown
          setTimeout(() => {
            tomselect.clear(true)
            tomselect.blur()

            // Also blur the actual input element
            const input = selectElement.parentElement.querySelector('input[id^="tomselect-"]')
            if (input) {
              input.blur()
            }
          }, 100)
        })
      }
    }, 500)
  }
}
